/**
 * Types for the Requests (intake-to-procure) page. Requests are plain data
 * (numbers and strings only) so they can live in a demo store and be cloned.
 */
import type {
  CategoryId,
  ContractId,
  MaterialId,
  PersonId,
  ProcurementType,
  SupplierId,
} from '../../data/types';

export const REQUEST_STAGES = [
  'intake',
  'review',
  'sourcing',
  'contracting',
  'ordering',
  'done',
] as const;

export type RequestStage = (typeof REQUEST_STAGES)[number];

export type RequestChannel = 'web' | 'teams' | 'slack' | 'email';

export type CheckId =
  | 'duplicate'
  | 'policy'
  | 'budget'
  | 'security'
  | 'legal'
  | 'supplier-risk';

/** pass = ok, flag = needs attention, fail = blocker, na = not required. */
export type CheckStatus = 'pass' | 'flag' | 'fail' | 'na' | 'pending';

export type CheckRef =
  | { kind: 'contract'; contractId: ContractId; clause?: string }
  | { kind: 'request'; requestId: string }
  | { kind: 'risk-alert'; alertId: string };

export interface RequestCheck {
  id: CheckId;
  status: CheckStatus;
  /** One line of reasoning. */
  summary: string;
  refs?: CheckRef[];
}

/** An approver who is one of the shared people, or a local named approver. */
export type Approver =
  | { kind: 'person'; id: PersonId }
  | { kind: 'named'; name: string; title: string };

export type ApprovalStatus = 'approved' | 'open' | 'changes_requested';

export interface ApprovalStep {
  id: string;
  role: string;
  approver: Approver;
  status: ApprovalStatus;
  /** Epoch ms of the decision. */
  decidedAt?: number;
  /** True when policy approved the step without a human. */
  auto?: boolean;
  note?: string;
}

export type ActivityActor = { kind: 'agent' } | Approver;

export interface ActivityEntry {
  id: string;
  /** Epoch ms. */
  at: number;
  actor: ActivityActor;
  text: string;
  detail?: string;
}

export type LinkedRecord =
  | { kind: 'onboarding'; caseId: string; title: string; detail: string }
  | { kind: 'sourcing-event'; eventId: string; title: string; detail: string }
  | { kind: 'sourcing-index'; title: string; detail: string }
  | { kind: 'purchase-order'; poNumber: string; title: string; detail: string }
  | { kind: 'contract'; contractId: ContractId; detail: string }
  | { kind: 'material'; materialId: MaterialId; detail: string }
  | { kind: 'request'; requestId: string; title: string; detail: string }
  | { kind: 'risk-alert'; alertId: string; title: string; detail: string };

export interface SuggestedSupplier {
  /** Set for suppliers in the shared supplier data. */
  supplierId?: SupplierId;
  /** Display name, used when there is no supplierId. */
  name: string;
  location?: string;
  /** One line on how this supplier relates to the request. */
  role: string;
  contractId?: ContractId;
  /** For suppliers outside the shared supplier data. */
  standing?: 'not-onboarded' | 'in-onboarding' | 'onboarded';
}

export interface RequestRecommendation {
  title: string;
  body: string;
}

export interface ProcurementRequest {
  id: string;
  title: string;
  description: string;
  requesterId: PersonId;
  /** Who the purchase is for, when different from the requester. */
  requestedFor?: string;
  channel: RequestChannel;
  department: string;
  costCenter: string;
  categoryId: CategoryId | null;
  categoryLabel: string;
  type: ProcurementType;
  /** USD. Null when the request does not state an amount. */
  amountUsd: number | null;
  amountNote?: string;
  /** True when the amount is per year. */
  recurring: boolean;
  /** ISO date or null. */
  neededBy: string | null;
  neededByNote?: string;
  /** Epoch ms. */
  createdAt: number;
  stage: RequestStage;
  stageNote?: string;
  skippedStages: RequestStage[];
  /** How the request will be bought: catalog order, RFP, amendment... */
  buyPath: string;
  suppliers: SuggestedSupplier[];
  checks: RequestCheck[];
  approvals: ApprovalStep[];
  linked: LinkedRecord[];
  activity: ActivityEntry[];
  /** People following the request without being an approver. */
  watchers?: PersonId[];
  recommendation?: RequestRecommendation;
  /** Stage the request moves to once every approval is done. */
  onApproved?: RequestStage;
  /** Agent message logged when the last approval lands. */
  onApprovedNote?: string;
}

export type RequestFilter =
  | 'all'
  | 'mine'
  | 'needs-approval'
  | 'direct'
  | 'indirect';
